import React, { useState, useRef } from 'react'
import {
    View,
    TextInput,
    TouchableOpacity,
    Text,
    StyleSheet,
    type TextInputProps,
} from 'react-native'
import { COLORS, SPACING, RADIUS } from '../constants/theme'
import { useAppTranslation } from '../i18n/useAppTranslation'
import { useWebInputName } from '../utils/useWebInputName'

interface PasswordFieldProps {
    label: string
    value: string
    onChangeText: (value: string) => void
    placeholder?: string
    error?: string
    editable?: boolean
    testID?: string
    autoComplete?: TextInputProps['autoComplete']
    textContentType?: TextInputProps['textContentType']
    nativeID?: string
    name?: string
    importantForAutofill?: TextInputProps['importantForAutofill']
    /** `onDark` — white label for green/dark auth screens (default). `onLight` — for white modal/panel backgrounds. */
    variant?: 'onDark' | 'onLight'
}

const PasswordField = ({
    label,
    value,
    onChangeText,
    placeholder,
    error,
    editable = true,
    testID,
    autoComplete = 'current-password',
    textContentType = 'password',
    nativeID,
    name,
    importantForAutofill = 'yes',
    variant = 'onDark',
}: PasswordFieldProps) => {
    const [visible, setVisible] = useState(false)
    const inputRef = useRef<TextInput>(null)
    const { t } = useAppTranslation()
    useWebInputName(inputRef, nativeID, name)

    return (
        <View style={styles.group}>
            <Text style={[styles.label, variant === 'onLight' && styles.labelOnLight]}>{label}</Text>
            <View style={[styles.inputRow, error && styles.inputError]}>
                <TextInput
                    ref={inputRef}
                    style={styles.input}
                    value={value}
                    onChangeText={onChangeText}
                    placeholder={placeholder}
                    placeholderTextColor={COLORS.form.placeholder}
                    editable={editable}
                    secureTextEntry={!visible}
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete={autoComplete}
                    textContentType={textContentType}
                    nativeID={nativeID}
                    importantForAutofill={importantForAutofill}
                    testID={testID}
                />
                <TouchableOpacity
                    style={styles.toggle}
                    onPress={() => setVisible(v => !v)}
                    accessibilityLabel={
                        visible ? t('passwordField.hidePassword') : t('passwordField.showPassword')
                    }
                    accessibilityRole="button"
                >
                    <Text style={styles.toggleText}>{visible ? '🙈' : '👁'}</Text>
                </TouchableOpacity>
            </View>
            {error ? <Text style={styles.errorText}>{error}</Text> : null}
        </View>
    )
}

const styles = StyleSheet.create({
    group: {
        marginBottom: SPACING.md,
    },
    label: {
        fontSize: 14,
        fontWeight: '600',
        color: COLORS.white,
        marginBottom: SPACING.xs,
    },
    labelOnLight: {
        color: COLORS.form.label,
    },
    inputRow: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: COLORS.form.inputBorder,
        borderRadius: RADIUS.md,
        backgroundColor: COLORS.form.inputBackground,
    },
    input: {
        flex: 1,
        paddingHorizontal: SPACING.md,
        paddingVertical: SPACING.sm,
        fontSize: 16,
        color: COLORS.form.inputText,
    },
    inputError: {
        borderColor: COLORS.form.inputBorderError,
    },
    toggle: {
        paddingHorizontal: SPACING.md,
        paddingVertical: SPACING.sm,
    },
    toggleText: {
        fontSize: 18,
    },
    errorText: {
        color: COLORS.error,
        fontSize: 12,
        marginTop: SPACING.xs,
    },
})

export default PasswordField
